import { useMemo, useState } from "react";
import { Check, Loader2, Plus, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthProvider";
import { BaseSheet } from "@/components/common/BaseSheet";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useTeamData } from "@/hooks/users/useTeamData";
import { failReportJob, trackReportJob } from "@/hooks/inboxView/useInboxViewReportJobs";
import { useTestSendViewReport } from "@/hooks/inboxView/useInboxViews";
import type { InboxView, InboxViewReportFormat } from "@/types/inboxView/inboxView";

export type ViewReportFrequency = "daily" | "weekly" | "fortnightly" | "monthly";

const REPORT_FORMATS: { value: InboxViewReportFormat; label: string }[] = [
  { value: "both", label: "PDF report and CSV file" },
  { value: "pdf", label: "PDF report only" },
  { value: "csv", label: "CSV file only" },
];

interface ViewReportScheduleProps {
  view: InboxView;
  isOpen: boolean;
  onClose: () => void;
  isSaving: boolean;
  onSave: (patch: {
    report_frequency: ViewReportFrequency | null;
    report_day_of_week: number | null;
    report_recipients: string[];
    report_format?: InboxViewReportFormat;
  }) => void;
  /** Emails pulled from the view's scoped agents' report_recipients. */
  agentSuggestedEmails?: string[];
}

const DAYS_OF_WEEK = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

// Mirrors ReportModeSelector's frequency/day-of-week/email-list pattern, but
// simplified to a single on/off toggle (no multi-mode radio) since a view's
// scheduled report is binary: on with a cadence, or off.
export const ViewReportSchedule = ({
  view,
  isOpen,
  onClose,
  isSaving,
  onSave,
  agentSuggestedEmails = [],
}: ViewReportScheduleProps) => {
  const { user, userProfile, team } = useAuth();
  const { data: teamData } = useTeamData(team?.id, team?.owner_id);
  const testSend = useTestSendViewReport();

  const [editing, setEditing] = useState(false);
  const [frequency, setFrequency] = useState<ViewReportFrequency>(view.report_frequency ?? "weekly");
  const [dayOfWeek, setDayOfWeek] = useState<number>(view.report_day_of_week ?? 1);
  const [recipients, setRecipients] = useState<string[]>(view.report_recipients ?? []);
  const [format, setFormat] = useState<InboxViewReportFormat>(view.report_format ?? "both");

  const isOn = !!view.report_frequency;

  const myEmail = user?.email ?? userProfile?.email ?? null;
  const teammateEmails = (teamData?.members ?? []).map((m) => m.email).filter((e): e is string => !!e);

  // De-duped, in priority order: me, teammates, agents' support team emails.
  // Anything already in the recipient list is dropped so we never suggest a dupe.
  const suggestedEmails = useMemo(() => {
    const current = new Set(recipients.map((e) => e.trim().toLowerCase()).filter(Boolean));
    const candidates = [
      ...(myEmail ? [myEmail] : []),
      ...teammateEmails,
      ...agentSuggestedEmails,
    ];
    const seen = new Set<string>();
    return candidates.filter((email) => {
      const key = email.trim().toLowerCase();
      if (!key || current.has(key) || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myEmail, teamData, agentSuggestedEmails, recipients]);

  const addSuggested = (email: string) => {
    setRecipients((prev) => {
      const emptyIdx = prev.findIndex((e) => !e.trim());
      if (emptyIdx !== -1) {
        const next = [...prev];
        next[emptyIdx] = email;
        return next;
      }
      return [...prev, email];
    });
  };

  const startEditing = () => {
    setFrequency(view.report_frequency ?? "weekly");
    setDayOfWeek(view.report_day_of_week ?? 1);
    setRecipients(view.report_recipients ?? []);
    setFormat(view.report_format ?? "both");
    setEditing(true);
  };

  const handleToggle = (checked: boolean) => {
    if (checked) {
      // First-time setup: switching on just opens the form — nothing is
      // saved (and the report isn't active) until "Save & activate" is
      // clicked. This mirrors an already-on schedule staying on until the
      // user explicitly turns it off below.
      startEditing();
      return;
    }
    onSave({ report_frequency: null, report_day_of_week: view.report_day_of_week ?? 1, report_recipients: view.report_recipients ?? [] });
  };

  const commit = () => {
    // Saving always turns the schedule on — there's no such thing as a saved
    // config that isn't active. Independent of the toggle: editing an
    // already-active schedule (changing cadence/recipients) just updates it
    // in place without any extra activation step.
    onSave({
      report_frequency: frequency,
      report_day_of_week: dayOfWeek,
      report_recipients: recipients.map((e) => e.trim()).filter(Boolean),
      report_format: format,
    });
    setEditing(false);
  };

  const handleClose = () => {
    setEditing(false);
    onClose();
  };

  const cleanRecipients = recipients.map((e) => e.trim()).filter(Boolean);

  const handleTestSend = () => {
    if (cleanRecipients.length === 0) return;
    const requestId = crypto.randomUUID();
    trackReportJob(
      requestId,
      toast.loading("Sending your test report. You can keep working, we'll let you know when it's sent."),
      cleanRecipients.length === 1 ? cleanRecipients[0] : `${cleanRecipients.length} recipients`,
    );
    testSend.mutate(
      {
        viewId: view.id,
        recipientEmails: cleanRecipients,
        reportFormat: editing ? format : view.report_format,
        requestId,
      },
      { onError: () => failReportJob(requestId, "Test report failed to send.") },
    );
  };

  return (
    <BaseSheet
      isOpen={isOpen}
      onClose={handleClose}
      title="Scheduled email report"
      description="Email this view's data on a recurring cadence, with a PDF report, a CSV file or both, and a summary of what changed."
      size="lg"
    >
      <div className="flex items-center gap-2">
        <Switch
          checked={isOn}
          onCheckedChange={handleToggle}
          disabled={isSaving}
          aria-label="Scheduled email report"
        />
        <span className="text-sm font-semibold text-[#222B45]">{isOn ? "On" : "Off"}</span>
      </div>
      {editing && !isOn && (
        <p className="text-xs text-genie-primary mt-1">
          Not active yet — set up the schedule below and click "Save & activate" to turn it on.
        </p>
      )}

      {(editing || isOn) && (
        <div className="mt-3 pl-4 border-l-2 border-genie-primary/30 space-y-3">
          {editing ? (
            <>
              <div>
                <Label className="text-xs font-medium text-[#64748B] mb-1.5 block">Frequency</Label>
                <Select value={frequency} onValueChange={(v) => setFrequency(v as ViewReportFrequency)}>
                  <SelectTrigger className="max-w-xs">
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="fortnightly">Fortnightly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {(frequency === "weekly" || frequency === "fortnightly") && (
                <div>
                  <Label className="text-xs font-medium text-[#64748B] mb-1.5 block">Send on</Label>
                  <Select value={String(dayOfWeek)} onValueChange={(v) => setDayOfWeek(Number(v))}>
                    <SelectTrigger className="max-w-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DAYS_OF_WEEK.map((d) => (
                        <SelectItem key={d.value} value={String(d.value)}>
                          {d.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label className="text-xs font-medium text-[#64748B] mb-1.5 block">Attach</Label>
                <Select value={format} onValueChange={(v) => setFormat(v as InboxViewReportFormat)}>
                  <SelectTrigger className="max-w-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REPORT_FORMATS.map((f) => (
                      <SelectItem key={f.value} value={f.value}>
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs font-medium text-[#64748B] mb-1.5 block">Email recipients</Label>
                <div className="space-y-2">
                  {recipients.map((email, index) => (
                    <div key={index} className="flex gap-2 min-w-0">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => {
                          const next = [...recipients];
                          next[index] = e.target.value;
                          setRecipients(next);
                        }}
                        className="flex-1 min-w-0 max-w-sm rounded-md border border-[#E5EAF0] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-genie-primary"
                        placeholder="team@example.com"
                      />
                      <button
                        type="button"
                        onClick={() => setRecipients(recipients.filter((_, i) => i !== index))}
                        className="flex items-center justify-center px-3 py-2 text-red-600 hover:bg-red-50 rounded-md border border-red-200 text-sm shrink-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setRecipients([...recipients, ""])}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-genie-primary hover:opacity-80"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add email
                  </button>
                  {suggestedEmails.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 pt-1">
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-[#B6C0CC]">Suggested:</span>
                      {suggestedEmails.map((email) => (
                        <button
                          key={email}
                          type="button"
                          onClick={() => addSuggested(email)}
                          className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border border-[#E5EAF0] bg-white text-[#64748B] hover:border-genie-primary/40 hover:text-genie-primary transition-colors"
                        >
                          <Plus className="h-2.5 w-2.5" />
                          {email}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={commit}
                  disabled={isSaving || cleanRecipients.length === 0}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-semibold disabled:opacity-50"
                  style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
                >
                  {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                  {isOn ? "Save schedule" : "Save & activate"}
                </button>
                <button
                  onClick={handleTestSend}
                  disabled={testSend.isPending || cleanRecipients.length === 0}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-[#64748B] border border-[#E5EAF0] hover:border-genie-primary/40 hover:text-genie-primary disabled:opacity-50"
                  title="Send a one-off test report now, without waiting for the schedule"
                >
                  {testSend.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                  Test
                </button>
                <button
                  onClick={() => setEditing(false)}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-[#64748B] hover:text-[#222B45]"
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <div className="text-xs text-[#64748B] space-y-1">
              <div>
                <span className="font-medium text-[#222B45] capitalize">{view.report_frequency}</span>
                {(view.report_frequency === "weekly" || view.report_frequency === "fortnightly") && (
                  <> on {DAYS_OF_WEEK.find((d) => d.value === (view.report_day_of_week ?? 1))?.label}</>
                )}
                {view.report_last_sent_at && <> · last sent {new Date(view.report_last_sent_at).toLocaleDateString()}</>}
              </div>
              <div>Attached: {REPORT_FORMATS.find((f) => f.value === (view.report_format ?? "both"))?.label}</div>
              {view.report_recipients.length > 0 && <div>To: {view.report_recipients.join(", ")}</div>}
              {(view.blocked_report_recipients ?? []).length > 0 && (
                <div className="text-red-600">
                  Blocked — bounced: {(view.blocked_report_recipients ?? []).join(", ")}
                </div>
              )}
              <div className="flex items-center gap-3 pt-0.5">
                <button onClick={startEditing} className="text-genie-primary font-semibold hover:opacity-80">
                  Edit schedule
                </button>
                <button
                  onClick={handleTestSend}
                  disabled={testSend.isPending || view.report_recipients.length === 0}
                  className="inline-flex items-center gap-1 font-semibold text-[#64748B] hover:text-genie-primary disabled:opacity-50"
                >
                  {testSend.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                  Send test now
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </BaseSheet>
  );
};
